import {
    TYPE_CONTRACT,
    TYPE_EVENT_MANAGEMENT,
    TYPE_GENAI,
    TYPE_GLOBAL_DASHBOARD,
    TYPE_HTML,
    TYPE_CRM,
} from '@/aquassist-shared/constants/types/features';

import $rq from '@/api/api';
import type { Team } from '@/interfaces/common';

interface TeamLang {
    key: string;
    optional: boolean;
}

interface TeamState {
    team: Team;
    selectedLang: string | null;
    teamDateFormat: string | null;
    showContractByFeature: boolean;
    showEventManagementByFeature: boolean;
    showGenAIByFeature: boolean;
    showGlobalDashboardByFeature: boolean;
    showHtmlReportByFeature: boolean;
    showCrmByFeature: boolean;
}

// Default state
const defaultState: TeamState = {
    team: {} as Team,
    selectedLang: null,
    teamDateFormat: null,
    showContractByFeature: false,
    showEventManagementByFeature: false,
    showGenAIByFeature: false,
    showGlobalDashboardByFeature: false,
    showHtmlReportByFeature: false,
    showCrmByFeature: false,
};

// Mutations
const mutations = {
    setTeamState(state: TeamState, team: Team) {
        state.team = team;
    },
    setShowContractByFeature(state: TeamState, showContractByFeature: boolean) {
        state.showContractByFeature = showContractByFeature;
    },
    setShowEventManagementByFeature(state: TeamState, show: boolean) {
        state.showEventManagementByFeature = show;
    },
    setShowGenAIByFeature(state: TeamState, showGenAIByFeature: boolean) {
        state.showGenAIByFeature = showGenAIByFeature;
    },
    setShowGlobalDashboardByFeature(state: TeamState, show: boolean) {
        state.showGlobalDashboardByFeature = show;
    },
    setShowHtmlReportByFeature(state: TeamState, show: boolean) {
        state.showHtmlReportByFeature = show;
    },
    setShowCrmByFeature(state: TeamState, show: boolean) {
        state.showCrmByFeature = show;
    },
    setSelectedLang(state: TeamState, selectedLang: string | null) {
        state.selectedLang = selectedLang;
    },
    setTeamDateFormat(state: TeamState, dateFormat: string | null) {
        state.teamDateFormat = dateFormat;
    },
    resetTeamState(state: TeamState) {
        Object.assign(state, defaultState);
    },
};

// Actions
const actions = {
    async reloadTeam(
        { commit }: { commit: (mutation: string, payload?: unknown) => void },
        teamId: string,
    ) {
        try {
            const { data } = await $rq.getTeam(teamId);
            const isContractFeature = data.activeFeatures?.some(
                (activeFeature) => activeFeature.feature?.type === TYPE_CONTRACT,
            );
            const isEventManagementFeature = data.activeFeatures?.some(
                (activeFeature) => activeFeature.feature?.type === TYPE_EVENT_MANAGEMENT,
            );
            const isGenAIFeature = data.activeFeatures?.some(
                (activeFeature) => activeFeature.feature?.type === TYPE_GENAI,
            );
            const isGlobalDashboardFeature = data.activeFeatures?.some(
                (activeFeature) => activeFeature.feature?.type === TYPE_GLOBAL_DASHBOARD,
            );
            const isHtmlReportFeature = data.activeFeatures?.some((activeFeature) => {
                return (
                    activeFeature.feature?.type === TYPE_HTML &&
                    !!activeFeature.feature?.activableTeams?.find((team) => team.team === teamId)
                );
            });
            const isCrmFeature = data.activeFeatures?.some(
                (activeFeature) => activeFeature.feature?.type === TYPE_CRM,
            );
            commit('setShowContractByFeature', isContractFeature);
            commit('setShowEventManagementByFeature', !!isEventManagementFeature);
            commit('setShowGenAIByFeature', isGenAIFeature);
            commit('setShowGlobalDashboardByFeature', !!isGlobalDashboardFeature);
            commit('setShowHtmlReportByFeature', !!isHtmlReportFeature);
            commit('setShowCrmByFeature', !!isCrmFeature);
            commit('setTeamState', data);
            commit('setSelectedLang', data.lang);
            commit('setTeamDateFormat', data.dateFormat || null);
        } catch (err) {
            commit('resetTeamState');
        }
    },
    resetTeamInfo({ commit }: { commit: (mutation: string) => void }) {
        commit('resetTeamState');
    },
    resetSelectedLang({ commit }: { commit: (mutation: string) => void }) {
        commit('resetTeamState');
    },
};

// Getters
const getters = {
    team: (state: TeamState): Team => state.team,
    showContractByFeature: (state: TeamState): boolean => state.showContractByFeature,
    showEventManagementByFeature: (state: TeamState): boolean => state.showEventManagementByFeature,
    showGenAIByFeature: (state: TeamState): boolean => state.showGenAIByFeature,
    showGlobalDashboardByFeature: (state: TeamState): boolean => state.showGlobalDashboardByFeature,
    showHtmlReportByFeature: (state: TeamState): boolean => state.showHtmlReportByFeature,
    showCrmByFeature: (state: TeamState): boolean => state.showCrmByFeature,
    teamOptionalLangs: (state: TeamState): string[] | undefined => state.team.optionalLangs,
    selectedLang: (state: TeamState): string | null => state.selectedLang,
    teamMainLang: (state: TeamState): string | undefined => state.team.lang,
    teamDateFormat: (state: TeamState): string | undefined => state.team.dateFormat,
    allTeamLangs: (state: TeamState): TeamLang[] => {
        const { lang, optionalLangs } = state.team;
        const allLangs: TeamLang[] = [];

        if (lang) {
            const mainLang: TeamLang = {
                key: lang,
                optional: false,
            };
            allLangs.push(mainLang);
        }

        if (optionalLangs) {
            optionalLangs.forEach((oLang) => {
                allLangs.push({
                    key: oLang,
                    optional: true,
                });
            });
        }

        return allLangs;
    },
};

export default {
    state: defaultState,
    getters,
    actions,
    mutations,
};
